<template>
    <div class="blog">
        <PageContentLoader v-if="(stale || fetching) && !collection" />

        <PageErrorFiveHundred
            v-else-if="error"
            :errors="responseErrors"
        >
            <ThButton
                :loading="fetching"
                @click="$emit('try-again')"
            >
                {{ t('globals.try_again') }}
            </ThButton>
        </PageErrorFiveHundred>

        <TransitionListGrid>
            <div
                v-if="collection"
                :key="collection.posts[0]?.id"
                :class="classBlogContent"
                class="blog-content"
            >
                <BlogHeader
                    v-if="categories"
                    :breadcrumbs="breadcrumbs"
                    :categories="categories"
                    :title="title"
                    class="blog-header"
                />

                <div
                    v-if="collection.posts"
                    class="blog-grid-container"
                >
                    <BlogGrid
                        :get-page-link="getPageLink"
                        :page="page"
                        :posts="collection.posts"
                        :total-count="collection.postAggregation.count"
                        :limit="limit"
                    />
                    <slot name="blocks"></slot>
                </div>

                <PageErrorFourHundredFour v-else />
            </div>
        </TransitionListGrid>

        <PageErrorFourHundredFour v-if="!collection && !fetching && !stale" />
    </div>
</template>

<script lang="ts" setup>
import { computed, toRef } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import type { CombinedError } from '@urql/vue'
import type { ButtonRouteRelative } from '@/components/ui/buttons/useThButton'
import { useMeta } from '@/composables/app/useMeta'
import type { ErrorCollectionWithTraces } from '@/helpers/errors'
import type {
    AuthorPostsFragment,
    BlogMenuFragment,
    BlogPostPreviewFragment,
    CategoryPostsFragment,
    TagPostsFragment,
} from '@/graphql/api'
import { useIsMobile } from '@/composables/app/useIsMobile'
import { useRoutes } from '@/router/routes'
import type { Breadcrumb } from '@/components/ui/Breadcrumbs.vue'

import PageContentLoader from '@/components/ui/PageContentLoader.vue'
import ThButton from '@/components/ui/buttons/ThButton.vue'
import BlogGrid from '@/components/cms/component/blog/BlogGrid.vue'
import BlogHeader from '@/components/cms/component/blog/BlogHeader.vue'
import PageErrorFiveHundred from '@/components/PageErrorFiveHundred.vue'
import PageErrorFourHundredFour from '@/components/PageErrorFourHundredFour.vue'
import TransitionListGrid from '@/transitions/TransitionListGrid.vue'
import { usePageCount } from '@/composables/page-count.ts'
import { usePageWatcher } from '@/composables/page-watcher.ts'

export type BlogPostCollection = {
    posts: BlogPostPreviewFragment[]
    postAggregation: {
        count: number
    }
}

const props = defineProps<{
    page: number
    categories: BlogMenuFragment[] | undefined
    collection:
        | BlogPostCollection
        | TagPostsFragment
        | AuthorPostsFragment
        | CategoryPostsFragment
        | undefined
        | null
    hasData: boolean
    title?: string
    metaTitle?: string
    description?: string
    breadcrumbTitle?: string
    fetching: boolean
    stale: boolean
    error: CombinedError | undefined
    responseErrors: ErrorCollectionWithTraces
}>()

defineEmits<{
    'try-again': []
}>()

const { t } = useI18n()
const router = useRouter()
const routes = useRoutes()

const { isMobile } = useIsMobile(768)

const breadcrumbs = computed((): Breadcrumb[] => {
    if (isMobile) {
        return []
    }

    // TODO translate
    const breadcrumbs = [
        {
            title: 'Home',
            to: routes.home(),
        },
        {
            title: 'Blog',
        },
    ] as Breadcrumb[]

    if (props.breadcrumbTitle) {
        if (breadcrumbs[1]) {
            breadcrumbs[1].to = routes.blog.list()
        }

        breadcrumbs.push({
            title: props.breadcrumbTitle,
        })
    }

    return breadcrumbs
})

useMeta(
    {
        title: () => props.metaTitle,
        description: () => props.description,
    },
    toRef(() => props.collection),
    toRef(() => props.fetching),
    toRef(() => props.stale)
)

const route = useRoute()

const getPageLink = (page: number): ButtonRouteRelative => ({
    name: route.name || undefined,
    params: {
        ...route.params,
        page: page === 1 ? undefined : page,
    },
})

const classBlogContent = computed(() => ({
    fetching: props.fetching,
}))

const limit = 10

const pageCount = usePageCount(
    () => props.collection?.postAggregation.count ?? 0,
    () => limit
)
const pageWatcher = usePageWatcher(
    () => props.page,
    pageCount,
    () => {
        return router.resolve(getPageLink(1)).fullPath
    },
    302
)

await pageWatcher()
</script>

<style lang="scss" scoped>
.blog {
    .blog-content {
        @include fetching;
    }
}
</style>
